import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ChevronDown } from "lucide-react"
import { useState } from "react"
import { MONRA_ESIM_DOWNLOAD_URL } from "@/lib/app-links"

// Feature Icon Card
interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  className?: string
  accentColor?: string
}

export function FeatureCard({ icon, title, description, className, accentColor }: FeatureCardProps) {
  return (
    <div className={cn("monra-card border border-border p-5 transition-colors duration-200 hover:bg-muted/55 sm:p-7", className)}>
      <div className={cn("mb-4 inline-flex rounded-xl p-3", accentColor || "bg-primary text-primary-foreground")}>
        {icon}
      </div>
      <h3 className="mb-2 font-sans text-base font-semibold tracking-tight text-foreground sm:text-lg">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
    </div>
  )
}

// Step Card
interface StepCardProps {
  number: number
  title: string
  description: string
  icon?: React.ReactNode
  accentColor?: string
}

export function StepCard({ number, title, description, icon, accentColor = "bg-primary text-primary-foreground" }: StepCardProps) {
  return (
    <div className="monra-card relative flex flex-col items-center border border-border p-7 text-center">
      <div className={cn("mb-5 flex h-12 w-12 items-center justify-center rounded-xl text-lg font-semibold", accentColor)}>
        {icon || number}
      </div>
      <h3 className="mb-2 font-sans text-lg font-semibold text-foreground">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
    </div>
  )
}

// Pricing Card
interface PricingCardProps {
  name: string
  data: string
  validity: string
  price: string
  features: string[]
  popular?: boolean
  accentColor?: string
  buttonClass?: string
}

export function PricingCard({ name, data, validity, price, features, popular, accentColor, buttonClass }: PricingCardProps) {
  return (
    <div className={cn(
      "monra-card relative p-6 transition-all",
      popular ? "border border-foreground" : "",
      popular && accentColor
    )}>
      {popular && (
        <div className={cn("absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-semibold", buttonClass)}>
          Most Popular
        </div>
      )}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">{name}</h3>
        <p className="text-sm text-muted-foreground">{validity}</p>
      </div>
      <div className="mb-4">
        <span className="text-3xl font-bold text-foreground">{price}</span>
      </div>
      <div className="mb-6 rounded-2xl bg-muted/55 p-3">
        <p className="text-center text-2xl font-bold text-foreground">{data}</p>
        <p className="text-center text-sm text-muted-foreground">High-speed data</p>
      </div>
      <ul className="mb-6 space-y-2">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
            <svg className={cn("h-4 w-4 shrink-0", accentColor ? accentColor.replace("border-", "text-") : "text-primary")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {feature}
          </li>
        ))}
      </ul>
      <Button asChild className={cn("w-full", buttonClass)}>
        <a href="#download">Select Plan</a>
      </Button>
    </div>
  )
}

// FAQ Item
interface FAQItemProps {
  question: string
  answer: string
  accentColor?: string
}

export function FAQItem({ question, answer, accentColor = "text-primary" }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="group border-b border-border/70 last:border-b-0">
      <button
        className="flex w-full items-center justify-between gap-4 px-5 py-6 text-left transition-colors hover:bg-muted/45 sm:px-7"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={cn(
          "text-base font-medium transition-colors",
          isOpen ? accentColor : "text-foreground"
        )}>
          {question}
        </span>
        <div className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-border transition-all",
          isOpen ? cn("bg-opacity-10", accentColor.replace("text-", "border-") + "/50", accentColor.replace("text-", "bg-") + "/10") : cn("group-hover:bg-opacity-5", accentColor.replace("text-", "group-hover:border-"), accentColor.replace("text-", "group-hover:bg-"))
        )}>
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-colors",
            isOpen && accentColor
          )} />
        </div>
      </button>
      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-out",
        isOpen ? "max-h-96 pb-5 opacity-100" : "max-h-0 pb-0 opacity-0"
      )}>
        <p className="px-4 text-sm leading-relaxed text-muted-foreground sm:px-6">{answer}</p>
      </div>
    </div>
  )
}

// FAQ Section
interface FAQSectionProps {
  title?: string
  subtitle?: string
  faqs: Omit<FAQItemProps, 'accentColor'>[]
  accentColor?: string
}

export function FAQSection({ title = "Frequently Asked Questions", subtitle, faqs, accentColor }: FAQSectionProps) {
  return (
    <section id="faq" className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-9 text-center sm:mb-12">
          <p className="esim-eyebrow monra-eyebrow mb-5">Support</p>
          <h2 className="display-title text-foreground">{title}</h2>
          {subtitle && <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">{subtitle}</p>}
        </div>
        <div className="monra-card overflow-hidden border border-border/65">
          {faqs.map((faq, i) => (
            <FAQItem key={i} {...faq} accentColor={accentColor} />
          ))}
        </div>
        <div className="mt-8 text-center sm:mt-10">
          <p className="text-sm text-muted-foreground">
            Still have questions?{" "}
            <a href="mailto:support@monralabs.com" className={cn("font-medium transition-colors hover:opacity-80 hover:underline", accentColor || "text-primary")}>
              Contact our support team
            </a>
          </p>
        </div>
      </div>
    </section>
  )
}

// CTA Section
interface CTASectionProps {
  title: string
  description: string
  buttonText?: string
  buttonClass?: string
  buttonHref?: string
  secondaryButtonText?: string
  secondaryButtonClass?: string
  secondaryButtonHref?: string
  showStoreBadges?: boolean
}

export function CTASection({
  title,
  description,
  buttonText,
  buttonClass,
  buttonHref = "#download",
  secondaryButtonText,
  secondaryButtonClass,
  secondaryButtonHref = "#features",
  showStoreBadges = true
}: CTASectionProps) {
  return (
    <section id="download" className="py-20 lg:py-28">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <h2 className="display-title text-foreground">{title}</h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">{description}</p>

        <div className="mt-10 flex flex-col items-center justify-center gap-8">
          {/* Action Area */}
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            {buttonText && (
              <Button asChild size="lg" className={cn("px-8", buttonClass)}>
                <a href={buttonHref}>{buttonText}</a>
              </Button>
            )}

            {showStoreBadges && (
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a href={MONRA_ESIM_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3 text-foreground transition-colors hover:bg-muted">
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <div className="text-left">
                    <p className="text-[10px] font-medium leading-none opacity-80">Download on the</p>
                    <p className="text-base font-semibold leading-tight">App Store</p>
                  </div>
                </a>
                <a href={MONRA_ESIM_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3 text-foreground transition-colors hover:bg-muted">
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  <div className="text-left">
                    <p className="text-[10px] font-medium leading-none opacity-80">Get it on</p>
                    <p className="text-base font-semibold leading-tight">Google Play</p>
                  </div>
                </a>
              </div>
            )}
          </div>

          {/* Secondary Action */}
          {secondaryButtonText && (
            <Button asChild size="lg" variant="outline" className={cn("px-10", secondaryButtonClass)}>
              <a href={secondaryButtonHref}>{secondaryButtonText}</a>
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}

// Testimonial Card
interface TestimonialCardProps {
  quote: string
  author: string
  role: string
  avatar?: string
}

export function TestimonialCard({ quote, author, role }: TestimonialCardProps) {
  return (
    <div className="monra-card p-6">
      <div className="mb-4 flex gap-1">
        {[...Array(5)].map((_, i) => (
          <svg key={i} className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <p className="mb-4 text-sm leading-relaxed text-muted-foreground">&quot;{quote}&quot;</p>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-semibold text-foreground">
          {author.split(' ').map(n => n[0]).join('')}
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{author}</p>
          <p className="text-xs text-muted-foreground">{role}</p>
        </div>
      </div>
    </div>
  )
}

// Section Header
interface SectionHeaderProps {
  badge?: string
  title: string
  description?: string
  className?: string
  badgeClass?: string
}

export function SectionHeader({ badge, title, description, className, badgeClass }: SectionHeaderProps) {
  return (
    <div className={cn("mx-auto max-w-3xl text-center", className)}>
      {badge && (
        <span className={cn("monra-eyebrow mb-5", badgeClass)}>
          {badge}
        </span>
      )}
      <h2 className="display-title text-balance text-foreground">{title}</h2>
      {description && (
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground text-pretty">{description}</p>
      )}
    </div>
  )
}

// Policy Header
interface PolicyHeaderProps {
  title: string
  subtitle?: string
  variant?: 'main' | 'esim' | 'remittance'
  effectiveDate?: string
}

export function PolicyHeader({ title, subtitle, variant = 'main', effectiveDate }: PolicyHeaderProps) {
  const badgeLabel = {
    main: "MonraLabs",
    esim: "Monra eSIM",
    remittance: "Monra Remittance",
  }[variant]

  return (
    <section className="bg-background py-12 sm:py-16">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <p className="mb-4 text-sm font-semibold text-muted-foreground">
          {badgeLabel}
        </p>
        <h1 className="legal-title mt-4 text-foreground">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
            {subtitle}
          </p>
        )}
        {effectiveDate && (
          <p className="mt-6 border-t border-border pt-4 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Effective date:</span> {effectiveDate}
          </p>
        )}
      </div>
    </section>
  )
}
